/**
 * Manage Provider Services Dialog
 *
 * Corporate-manager surface for ticket 86eya6x30: toggle which services an individual
 * provider is authorized to offer. Saving writes ProfileProvider.capable_services, which
 * dispatch already gates on fail-closed — so disabling a service here immediately stops
 * that provider receiving dispatches for it.
 *
 * Reads:  GET /api/providers/<uuid>/services/  +  GraphQL serviceTemplates
 * Writes: PUT /api/providers/<uuid>/services/  (full replacement set)
 *
 * Controlled dumb component: the parent owns the mutation, same contract as
 * EditProviderDialog / DeleteProviderDialog.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, Loader2, Save, Search } from 'lucide-react'
import type { OrganizationProviderRow } from '@/hooks/useOrganizationProviders'
import { useProviderServices } from '@/hooks/useProviderServices'
import { useServiceCatalog } from '@/hooks/useServiceCatalog'
import { useLocalization } from '@/contexts/LocalizationContext'
import { decodeRelayId } from '@/utils/id'
import { formatCurrency } from '@/utils/formatters'
import { ApiError } from '@/api/errorHandler'
import { secondsToMinutes } from '@/types/providerServices'
import type { ServiceCatalogItem } from '@/types/providerServices'

interface ManageProviderServicesDialogProps {
  provider: OrganizationProviderRow | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (providerId: string, serviceTemplateIds: string[]) => Promise<void>
  isLoading?: boolean
}

/** A catalog row, plus the ones the provider holds that are no longer in the catalog. */
interface ServiceRow {
  id: string
  title: string
  categoryName: string
  price: number | null
  durationMinutes: number | null
  /** True when the provider already holds it but it has aged out of the active catalog. */
  isLegacy: boolean
}

export function ManageProviderServicesDialog({
  provider,
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: ManageProviderServicesDialogProps) {
  const { t, locale } = useLocalization()

  const providerUuid = provider?.id ? decodeRelayId(provider.id) ?? provider.id : null

  const {
    services: authorized,
    isLoading: isLoadingAuthorized,
    isError: isAuthorizedError,
    error: authorizedError,
    refetch: refetchAuthorized,
  } = useProviderServices(providerUuid, open)

  const {
    catalog,
    isLoading: isLoadingCatalog,
    isError: isCatalogError,
    refetch: refetchCatalog,
  } = useServiceCatalog(open)

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [seed, setSeed] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const [confirmDiscard, setConfirmDiscard] = useState(false)

  const isLoadingData = isLoadingAuthorized || isLoadingCatalog
  const permissionDenied =
    authorizedError instanceof ApiError && authorizedError.status === 403
  const providerMissing =
    authorizedError instanceof ApiError && authorizedError.code === 'PROVIDER_NOT_FOUND'

  /**
   * Stable identity for the authorized set. Depending on `authorized.length` would miss a
   * same-size change, and depending on the array itself re-fires every render because the
   * queryFn returns a fresh array.
   */
  const authorizedKey = useMemo(
    () => authorized.map((s) => s.id).sort().join(','),
    [authorized]
  )

  // Seed the selection from the server once the authorized set arrives, and reset
  // whenever the dialog is reopened or pointed at a different provider. Gating on
  // isLoadingAuthorized matters: seeding an empty set early would read as dirty and a
  // fast Save would clear every service.
  useEffect(() => {
    if (!open || isLoadingAuthorized || isAuthorizedError) return
    const ids = new Set(authorizedKey ? authorizedKey.split(',') : [])
    setSelected(ids)
    setSeed(ids)
  }, [open, providerUuid, isLoadingAuthorized, isAuthorizedError, authorizedKey])

  useEffect(() => {
    if (!open) {
      setSearch('')
      setConfirmDiscard(false)
    }
  }, [open])

  const rows = useMemo<ServiceRow[]>(() => {
    const uncategorized = t('providerServices.uncategorized')

    const fromCatalog = catalog.map((item) => toRow(item, locale, uncategorized))

    // A provider can hold a service that has aged out of the active catalog (the
    // catalog also filters on the effective/expiry window). Dropping those would let
    // the first Save silently revoke them, so surface them explicitly instead.
    const catalogIds = new Set(catalog.map((c) => c.id))
    const legacy = authorized
      .filter((s) => !catalogIds.has(s.id))
      .map<ServiceRow>((s) => ({
        id: s.id,
        title: s.label || s.name,
        categoryName: uncategorized,
        price: s.base_price != null ? Number(s.base_price) : null,
        // REST gives float seconds here; the catalog rows above are already minutes.
        durationMinutes: secondsToMinutes(s.estimated_duration),
        isLegacy: true,
      }))

    return [...fromCatalog, ...legacy]
  }, [catalog, authorized, locale, t])

  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase()
    const matching = term
      ? rows.filter((r) => r.title.toLowerCase().includes(term))
      : rows

    const byCategory = new Map<string, ServiceRow[]>()
    for (const row of matching) {
      const bucket = byCategory.get(row.categoryName)
      if (bucket) bucket.push(row)
      else byCategory.set(row.categoryName, [row])
    }

    return Array.from(byCategory.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [rows, search])

  const isDirty = useMemo(() => {
    if (selected.size !== seed.size) return true
    for (const id of selected) if (!seed.has(id)) return true
    return false
  }, [selected, seed])

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /**
   * Escape, overlay click and the built-in X all route through onOpenChange, so both the
   * mid-save lock and the unsaved-changes check have to live here rather than on Cancel.
   */
  const handleOpenChange = (next: boolean) => {
    if (next) return onOpenChange(true)
    if (isLoading) return
    if (isDirty) return setConfirmDiscard(true)
    onOpenChange(false)
  }

  const handleSave = async () => {
    if (!provider?.id) return
    try {
      await onSubmit(provider.id, Array.from(selected))
      onOpenChange(false)
    } catch {
      // The mutation hook already toasted; keep the dialog open with the selection intact.
    }
  }

  if (!provider) return null

  const displayName = provider.fullName || provider.name || t('providers.thisProvider')

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
        {/* pe-10 keeps the title clear of the dialog's close button, which is pinned right */}
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
          <DialogTitle className="pe-10">{t('providerServices.dialogTitle')}</DialogTitle>
          <DialogDescription>
            {t('providerServices.dialogDescription', { name: displayName })}
          </DialogDescription>
        </DialogHeader>

        {permissionDenied || providerMissing ? (
          // Neither is worth a Retry button — retrying will never change the answer.
          <div className="px-6 pb-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {permissionDenied
                  ? t('providerServices.permissionDenied')
                  : t('providerServices.providerNotFound')}
              </AlertDescription>
            </Alert>
          </div>
        ) : isAuthorizedError || isCatalogError ? (
          <div className="px-6 pb-6 space-y-3">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {(authorizedError as Error)?.message || t('providerServices.loadFailed')}
              </AlertDescription>
            </Alert>
            <Button
              variant="outline"
              onClick={() => {
                void refetchAuthorized()
                void refetchCatalog()
              }}
            >
              {t('common.retry')}
            </Button>
          </div>
        ) : (
          <>
            <div className="px-6 pb-3 shrink-0">
              <div className="relative">
                <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('providerServices.searchServices')}
                  className="ps-10"
                  disabled={isLoadingData}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-4">
              {isLoadingData ? (
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton className="h-4 w-4 rounded" />
                      <Skeleton className="h-4 w-48" />
                    </div>
                  ))}
                </div>
              ) : grouped.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {search.trim()
                    ? t('providerServices.noMatchingServices')
                    : t('providerServices.noServicesInCatalog')}
                </p>
              ) : (
                <div className="space-y-5">
                  {grouped.map(([category, items]) => (
                    <div key={category}>
                      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {category}
                      </h4>
                      <ul className="divide-y divide-border/80 rounded-lg border">
                        {items.map((item) => (
                          <li key={item.id}>
                            <label
                              htmlFor={`svc-${item.id}`}
                              className="flex cursor-pointer items-center gap-3 p-3 rtl:space-x-reverse hover:bg-muted/50"
                            >
                              <Checkbox
                                id={`svc-${item.id}`}
                                checked={selected.has(item.id)}
                                onCheckedChange={() => toggle(item.id)}
                                disabled={isLoading}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium">
                                  {item.title}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {[
                                    item.price != null
                                      ? formatCurrency(item.price, locale)
                                      : null,
                                    item.durationMinutes != null
                                      ? t('providerServices.minutesShort', {
                                          count: item.durationMinutes,
                                        })
                                      : null,
                                    item.isLegacy
                                      ? t('providerServices.notInCatalog')
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </div>
                              </div>
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selected.size === 0 && seed.size > 0 && (
              <div className="px-6 pb-3 shrink-0">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {t('providerServices.clearAllWarning')}
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {confirmDiscard ? (
              // No AlertDialog primitive exists and nesting Radix dialogs is fiddly,
              // so the confirmation replaces the footer in place.
              <DialogFooter className="flex-row items-center gap-3 border-t px-6 py-4 shrink-0">
                <span className="me-auto text-sm text-muted-foreground">
                  {t('providerServices.unsavedChanges')}
                </span>
                <Button variant="outline" onClick={() => setConfirmDiscard(false)}>
                  {t('providerServices.keepEditing')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setConfirmDiscard(false)
                    onOpenChange(false)
                  }}
                >
                  {t('providerServices.discard')}
                </Button>
              </DialogFooter>
            ) : (
              <DialogFooter className="flex-row items-center justify-between gap-3 border-t px-6 py-4 shrink-0">
                <span className="text-sm text-muted-foreground">
                  {t('providerServices.selectedCount', { count: selected.size })}
                </span>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => handleOpenChange(false)}
                    disabled={isLoading}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={!isDirty || isLoading || isLoadingData}
                    className="min-w-[140px]"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin me-2" />
                        {t('common.saving')}
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 me-2" />
                        {t('providerServices.saveChanges')}
                      </>
                    )}
                  </Button>
                </div>
              </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function toRow(
  item: ServiceCatalogItem,
  locale: string,
  uncategorized: string
): ServiceRow {
  const localizedName = locale === 'ar' ? item.nameAr : item.nameEn
  const rawPrice = item.pricingStrategy?.basePrice
  return {
    id: item.id,
    title: item.label || localizedName || item.name,
    categoryName: item.category?.name || uncategorized,
    price: rawPrice != null && rawPrice !== '' ? Number(rawPrice) : null,
    durationMinutes: item.estimatedDuration ?? null,
    isLegacy: false,
  }
}
